/**
 * Terminal-style primitives. Sharper corners, denser, dark by default.
 */
import {
  forwardRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";

type Variant = "primary" | "secondary" | "danger" | "ghost" | "buy" | "sell";

const VARIANT: Record<Variant, string> = {
  primary:
    "bg-brand text-black hover:bg-brand/90 focus-visible:ring-brand/40 disabled:bg-brand/50 font-semibold",
  secondary:
    "bg-term-elev text-term-fg hover:bg-term-border-strong border border-term-border focus-visible:ring-term-border-strong",
  danger:
    "bg-term-down text-white hover:bg-red-500 focus-visible:ring-red-400 disabled:opacity-50",
  ghost:
    "bg-transparent text-term-dim hover:text-term-fg hover:bg-term-elev",
  buy:
    "bg-term-up text-black hover:brightness-110 focus-visible:ring-emerald-400 font-semibold",
  sell:
    "bg-term-down text-white hover:brightness-110 focus-visible:ring-red-400 font-semibold",
};

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; loading?: boolean }
>(function Button({ variant = "primary", loading, disabled, className = "", children, ...rest }, ref) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-1.5 rounded-sm px-2.5 py-1 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-1 disabled:cursor-not-allowed ${VARIANT[variant]} ${className}`}
      {...rest}
    >
      {loading && (
        <svg
          className="h-3 w-3 animate-spin"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
        >
          <circle cx="12" cy="12" r="10" className="opacity-25" />
          <path d="M22 12a10 10 0 0 1-10 10" className="opacity-75" />
        </svg>
      )}
      {children}
    </button>
  );
});

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className = "", ...rest }, ref) {
    return (
      <input
        ref={ref}
        className={`block w-full rounded-sm border border-term-border bg-term-bg px-2 py-1 text-xs text-term-fg placeholder:text-term-mute focus:border-brand focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
        {...rest}
      />
    );
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className = "", children, ...rest }, ref) {
    return (
      <select
        ref={ref}
        className={`block w-full rounded-sm border border-term-border bg-term-bg px-2 py-1 text-xs text-term-fg focus:border-brand focus:outline-none focus:ring-0 ${className}`}
        {...rest}
      >
        {children}
      </select>
    );
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className = "", ...rest }, ref) {
    return (
      <textarea
        ref={ref}
        className={`block w-full rounded-sm border border-term-border bg-term-bg px-2 py-1 text-xs font-mono text-term-fg focus:border-brand focus:outline-none focus:ring-0 ${className}`}
        {...rest}
      />
    );
  },
);

export function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <div className={`term-card ${className}`}>{children}</div>;
}

export function CardHeader({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`border-b border-term-border bg-term-elev px-3 py-2 text-2xs uppercase tracking-wider text-term-dim ${className}`}>
      {children}
    </div>
  );
}

export function CardBody({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <div className={`p-3 ${className}`}>{children}</div>;
}

export function Label({ children, htmlFor }: { children: React.ReactNode; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="mb-1 block text-2xs font-medium uppercase tracking-wider text-term-dim">
      {children}
    </label>
  );
}

export function FormError({ children }: { children?: React.ReactNode }) {
  if (!children) return null;
  return (
    <div className="mt-2 rounded-sm border border-red-800 bg-red-950/40 px-2 py-1.5 text-xs text-red-300">
      {children}
    </div>
  );
}

export function Badge({
  tone = "neutral",
  children,
}: {
  tone?: "neutral" | "good" | "warn" | "bad";
  children: React.ReactNode;
}) {
  const tones: Record<typeof tone, string> = {
    neutral: "bg-term-elev text-term-dim border border-term-border",
    good: "bg-emerald-900/40 text-emerald-300 border border-emerald-800",
    warn: "bg-amber-900/40 text-amber-300 border border-amber-800",
    bad: "bg-red-900/40 text-red-300 border border-red-800",
  };
  return (
    <span className={`inline-flex items-center rounded-sm px-1.5 py-0.5 text-2xs font-medium uppercase tracking-wider ${tones[tone]}`}>
      {children}
    </span>
  );
}

/** Skeleton placeholder. Use `<Skeleton className="h-4 w-24" />` etc. */
export function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`skeleton ${className}`} />;
}

/** Empty-state block — icon + headline + optional CTA. */
export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-10 text-center">
      <div className="mb-3 text-term-mute">{icon ?? <DefaultEmptyIcon />}</div>
      <h3 className="mb-1 text-sm font-semibold text-term-fg">{title}</h3>
      {description && <p className="mb-3 max-w-sm text-xs text-term-dim">{description}</p>}
      {action}
    </div>
  );
}

function DefaultEmptyIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="3" width="18" height="18" rx="1" />
      <path d="M3 9h18M9 21V9" />
    </svg>
  );
}
